import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, CreditCard, TrendingUp, BarChart3 } from 'lucide-react';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import adminClient from '@/lib/api/adminClient';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/utils';

export function UserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creditOpen, setCreditOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [newPlan, setNewPlan] = useState('');

  const { data: user, isLoading } = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => adminClient.get(`/admin/users/${id}/detail`).then((r) => r.data),
  });

  const creditMutation = useMutation({
    mutationFn: () =>
      adminClient.put(`/admin/users/${id}/credits`, {
        amount: parseFloat(creditAmount),
        reason: creditReason || '手動調整',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-detail', id] });
      setCreditOpen(false);
      setCreditAmount('');
      setCreditReason('');
    },
  });

  const planMutation = useMutation({
    mutationFn: () => adminClient.put(`/admin/users/${id}/plan`, { plan: newPlan }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-detail', id] });
      setPlanOpen(false);
    },
  });

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  if (!user) {
    return <div className="text-center py-12 text-muted-foreground">找不到此用戶</div>;
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/users')} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> 返回用戶列表
      </Button>

      {/* User Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>用戶資料</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">電子郵件：</span> <strong>{user.email}</strong></div>
              <div><span className="text-muted-foreground">名稱：</span> <strong>{user.full_name || '-'}</strong></div>
              <div><span className="text-muted-foreground">方案：</span> <Badge>{user.plan || 'trial'}</Badge></div>
              <div><span className="text-muted-foreground">到期日：</span> {user.plan_expires_at ? formatDate(user.plan_expires_at) : '無'}</div>
              <div><span className="text-muted-foreground">狀態：</span> <Badge variant={user.is_active ? 'success' : 'destructive'}>{user.is_active ? '啟用中' : '已停權'}</Badge></div>
              <div><span className="text-muted-foreground">註冊日期：</span> {formatDate(user.created_at)}</div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button size="sm" variant="outline" onClick={() => setCreditOpen(true)}>調整點數</Button>
              <Button size="sm" variant="outline" onClick={() => { setNewPlan(user.plan || 'trial'); setPlanOpen(true); }}>變更方案</Button>
            </div>
          </CardContent>
        </Card>

        {/* Credits Summary */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">點數餘額</p>
                <p className="text-xl font-bold">{user.credits_balance.toFixed(0)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">累計獲得</p>
                <p className="text-xl font-bold">{user.total_earned.toFixed(0)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">回測次數</p>
                <p className="text-xl font-bold">{user.backtest_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="payments">
        <TabsList>
          <TabsTrigger value="payments">付款紀錄（{user.payment_count}）</TabsTrigger>
        </TabsList>
        <TabsContent value="payments">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>編號</TableHead>
                    <TableHead>付款方式</TableHead>
                    <TableHead>金額</TableHead>
                    <TableHead>點數</TableHead>
                    <TableHead>狀態</TableHead>
                    <TableHead>日期</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(user.recent_payments || []).map((tx: any) => (
                    <TableRow key={tx.id}>
                      <TableCell>#{tx.id}</TableCell>
                      <TableCell><Badge variant="outline">{tx.provider}</Badge></TableCell>
                      <TableCell>{formatCurrency(tx.amount)}</TableCell>
                      <TableCell>{tx.credits_amount.toFixed(0)}</TableCell>
                      <TableCell>
                        <Badge variant={tx.status === 'success' ? 'success' : tx.status === 'failed' || tx.status === 'cancelled' ? 'destructive' : tx.status === 'refunded' ? 'outline' : 'secondary'}>
                          {({ success: '成功', failed: '失敗', pending: '處理中', cancelled: '已取消', refunded: '退款' } as Record<string, string>)[tx.status] ?? tx.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDateTime(tx.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Credit Adjust Dialog */}
      <Dialog open={creditOpen} onOpenChange={setCreditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>調整點數</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">數量（正數為增加，負數為扣除）</label>
              <Input type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} placeholder="100" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">原因</label>
              <Input value={creditReason} onChange={(e) => setCreditReason(e.target.value)} placeholder="手動調整" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditOpen(false)}>取消</Button>
            <Button onClick={() => creditMutation.mutate()} disabled={!creditAmount || creditMutation.isPending}>
              {creditMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              確認
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Change Dialog */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>變更方案</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <Select value={newPlan} onValueChange={setNewPlan}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trial">試用版</SelectItem>
                <SelectItem value="starter">入門版</SelectItem>
                <SelectItem value="pro">專業版</SelectItem>
                <SelectItem value="expert">專家版</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanOpen(false)}>取消</Button>
            <Button onClick={() => planMutation.mutate()} disabled={planMutation.isPending}>
              {planMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              確認變更
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
